import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamRevisionBotexamidLangGridQueryKey,
  usePostApiV1ExamRevisionLang,
} from '@/shared/api/generated';
import type { CreateBotExamLangViewModel } from '@/shared/api/generated/types/CreateBotExamLangViewModel';

/**
 * Adiciona um idioma a uma prova em revisão via
 * `POST /api/v1/exam/revision/lang`, invalidando a grid de idiomas ao concluir.
 *
 * @param botExamId - ID da prova bot à qual o idioma será associado
 * @returns Handler de criação e estado de loading
 *
 * @example
 * ```tsx
 * const { createLang, isLoading } = useCreateRevisionLang(botExamId);
 * createLang({ botExamId, cultureInfo: 'en-US', instruction, active: true });
 * ```
 */
export function useCreateRevisionLang(botExamId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examRevision');

  const mutation = usePostApiV1ExamRevisionLang({
    mutation: {
      onSuccess: () => {
        alert({ alertType: 'success', title: t('langs.toast.addSuccess') });
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamRevisionBotexamidLangGridQueryKey(botExamId),
        });
      },
      onError: () => alert({ alertType: 'warning', title: t('langs.toast.addError') }),
    },
  });

  const createLang = (data: CreateBotExamLangViewModel, callbacks?: { onSuccess?: () => void }) => {
    mutation.mutate(
      { data },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    );
  };

  return { createLang, isLoading: mutation.isPending } as const;
}
